import type { MissingTalentSummary } from "@/lib/talent-tracking-api"

import { Tag } from "@/components/shared/tag"

import { CATEGORY_LABELS, CATEGORY_VALUES } from "./constants"
import { MissingTalentCard } from "./missing-talent-card"

export function TalentCardsView({
  visibleTalents,
  selectedCategory,
  cardSortMode,
  totalMembers,
}: {
  visibleTalents: MissingTalentSummary[]
  selectedCategory: string
  cardSortMode: string
  totalMembers: number
}) {
  return (
    <div
      className={`grid items-start gap-4 ${
        selectedCategory === "all"
          ? "md:grid-cols-2 2xl:grid-cols-3"
          : "max-w-5xl"
      }`}
    >
      {CATEGORY_VALUES.filter(
        (category) =>
          selectedCategory === "all" || category === selectedCategory,
      ).map((category) => {
        const talents = [...visibleTalents]
          .filter((talent) => talent.category === category)
          .sort((left, right) => {
            if (cardSortMode === "severity") {
              if (right.missingCount !== left.missingCount) {
                return right.missingCount - left.missingCount
              }
              if (left.tier !== right.tier) {
                return left.tier - right.tier
              }
              return left.description.localeCompare(right.description)
            }

            if (left.tier !== right.tier) {
              return left.tier - right.tier
            }
            if (right.missingCount !== left.missingCount) {
              return right.missingCount - left.missingCount
            }
            return left.description.localeCompare(right.description)
          })
        const missingSlots = talents.reduce(
          (total, talent) => total + talent.missingCount,
          0,
        )
        const impactedMembers = new Set(
          talents.flatMap((talent) => talent.missingUserIds),
        ).size

        return (
          <div
            key={category}
            className="grid gap-3 rounded-[1.2rem] border border-border/50 bg-linear-to-b from-card/70 to-background/65 p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-kicker text-xs text-muted-foreground">
                {CATEGORY_LABELS[category]}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-[0.68rem] text-muted-foreground">
                <Tag className="bg-background/70 px-2 py-1">
                  {talents.length} talents
                </Tag>
                <Tag className="bg-background/70 px-2 py-1">
                  {missingSlots} missing slots
                </Tag>
                <Tag className="bg-background/70 px-2 py-1">
                  {impactedMembers} impacted
                </Tag>
              </div>
            </div>
            {talents.length > 0 ? (
              talents.map((talent) => (
                <MissingTalentCard
                  key={talent.id}
                  talent={talent}
                  totalMembers={totalMembers}
                />
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-border/70 px-3 py-4 text-sm text-muted-foreground">
                No talents match this filter.
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
